import { FunctionalLocation } from '@easydone/shared';
import { ExclamationTriangleIcon, XCircleIcon } from '@heroicons/react/24/outline';

interface ConfirmFlocMoveDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  movingFloc: FunctionalLocation;
  targetParent: FunctionalLocation | null; // null = moving to root
  descendantCount: number;
  isValidMove: boolean;
  validationError?: string;
}

export function ConfirmFlocMoveDialog({
  isOpen,
  onClose,
  onConfirm,
  movingFloc,
  targetParent,
  descendantCount,
  isValidMove,
  validationError,
}: ConfirmFlocMoveDialogProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
        <h2 className="text-lg font-bold text-gray-900 mb-4">Confirm FLOC Move</h2>

        {/* Move Summary */}
        <div className="mb-4 space-y-2">
          <div className="flex items-start">
            <span className="text-sm text-gray-600 w-20">Moving:</span>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">{movingFloc.name}</p>
              <p className="text-xs text-gray-500">{movingFloc.code}</p>
            </div>
          </div>

          <div className="flex items-start">
            <span className="text-sm text-gray-600 w-20">To:</span>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">
                {targetParent ? targetParent.name : 'Root Level'}
              </p>
              {targetParent && (
                <p className="text-xs text-gray-500">{targetParent.code}</p>
              )}
            </div>
          </div>
        </div>

        {/* Descendant Warning */}
        {descendantCount > 0 && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex gap-3">
            <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-yellow-900">
                This will also move {descendantCount} descendant location{descendantCount !== 1 ? 's' : ''}
              </p>
              <p className="text-xs text-yellow-700 mt-1">
                All child locations will be moved along with their parent.
              </p>
            </div>
          </div>
        )}

        {/* Validation Error */}
        {!isValidMove && validationError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex gap-3">
            <XCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-900">Invalid Move</p>
              <p className="text-xs text-red-700 mt-1">{validationError}</p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 justify-end mt-6">
          <button
            onClick={onClose}
            className="btn btn-secondary"
            type="button"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!isValidMove}
            className="btn btn-primary"
            type="button"
          >
            Confirm Move
          </button>
        </div>
      </div>
    </div>
  );
}
